// Six digit boxes for an emailed code (WEB_PLAN.md §5.1): typing moves to the next box, Backspace goes back, and a
// pasted or autofilled code ("123 456", "123-456") fills every box at once (then `onComplete` fires, like iOS
// verifying a pasted code straight away).

import { clsx } from 'clsx';
import { useEffect, useRef, type ClipboardEvent, type KeyboardEvent } from 'react';

const LENGTH = 6;

export function CodeInput({
  value,
  onChange,
  onComplete,
  disabled,
  autoFocus,
}: {
  value: string;
  onChange: (digits: string) => void;
  /** Called when a whole code arrives at once (paste / autofill). */
  onComplete?: (digits: string) => void;
  disabled?: boolean;
  autoFocus?: boolean;
}) {
  const refs = useRef<(HTMLInputElement | null)[]>([]);
  const digits = value.replace(/\D/g, '').slice(0, LENGTH);

  useEffect(() => {
    if (autoFocus) refs.current[Math.min(digits.length, LENGTH - 1)]?.focus();
    // Only on mount / when re-enabled.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoFocus, disabled]);

  const focusBox = (i: number) => refs.current[Math.max(0, Math.min(LENGTH - 1, i))]?.focus();

  const setFrom = (index: number, typed: string) => {
    const incoming = typed.replace(/\D/g, '');
    if (incoming === '') return;
    if (incoming.length >= LENGTH) {
      // Autofill (one-time-code) or a paste into one box: a whole code at once.
      const code = incoming.slice(0, LENGTH);
      onChange(code);
      focusBox(LENGTH - 1);
      onComplete?.(code);
      return;
    }
    const chars = digits.padEnd(LENGTH, ' ').split('');
    let at = index;
    for (const d of incoming) {
      if (at >= LENGTH) break;
      chars[at] = d;
      at += 1;
    }
    const next = chars.join('').replace(/\s+$/, '').replace(/\s/g, '');
    onChange(next.slice(0, LENGTH));
    focusBox(at);
  };

  const onKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      e.preventDefault();
      if (index < digits.length) {
        onChange(digits.slice(0, index) + digits.slice(index + 1));
        focusBox(index);
      } else if (digits.length > 0) {
        onChange(digits.slice(0, -1));
        focusBox(digits.length - 1);
      }
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      focusBox(index - 1);
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      focusBox(index + 1);
    }
  };

  const onPaste = (index: number, e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text');
    const pasted = text.replace(/\D/g, '');
    if (pasted.length >= LENGTH) {
      const code = pasted.slice(0, LENGTH);
      onChange(code);
      focusBox(LENGTH - 1);
      onComplete?.(code);
    } else {
      setFrom(index, pasted);
    }
  };

  return (
    <div className="flex justify-between gap-2" role="group" aria-label="One-time code">
      {Array.from({ length: LENGTH }, (_, i) => (
        <input
          key={i}
          ref={(el) => {
            refs.current[i] = el;
          }}
          value={digits[i] ?? ''}
          onChange={(e) => {
            const raw = e.target.value;
            const current = digits[i] ?? '';
            if (raw.replace(/\D/g, '') === '') {
              // The box was emptied. Mobile keyboards often send no Backspace keydown (Android reports
              // "Unidentified"), so deleting arrives here instead of in onKeyDown.
              if (current !== '') onChange(digits.slice(0, i) + digits.slice(i + 1));
              return;
            }
            // Typing into a filled box: keep only the new digit(s).
            setFrom(i, raw.replace(current, '') || raw);
          }}
          onKeyDown={(e) => onKeyDown(i, e)}
          onPaste={(e) => onPaste(i, e)}
          onFocus={(e) => e.target.select()}
          disabled={disabled}
          inputMode="numeric"
          autoComplete={i === 0 ? 'one-time-code' : 'off'}
          maxLength={i === 0 ? LENGTH : 2}
          aria-label={`Digit ${i + 1} of ${LENGTH}`}
          className={clsx(
            'h-14 w-full min-w-0 rounded-xl border bg-slate-50 text-center text-2xl font-bold text-slate-900 tabular-nums outline-none transition',
            'focus:border-[var(--gt-orange)] focus:bg-white focus:ring-2 focus:ring-[var(--gt-orange)]/25',
            'disabled:opacity-60 dark:bg-slate-800 dark:text-slate-100 dark:focus:bg-slate-900',
            digits[i] ? 'border-slate-300 dark:border-slate-600' : 'border-slate-200 dark:border-slate-700',
          )}
        />
      ))}
    </div>
  );
}
